import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { userSchemas } from '@/api/users/user.schema';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { CopyText } from '@/components/ui/copy-text';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormSelect } from '@/components/ui/form-fields/form-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { userRoleOptions } from '@/db/schema/users';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useUpdateUser, useUser } from '@/services/users';
import type { ValidationError } from '@/types/api/validation-error';

export const Route = createFileRoute('/_app/users/$id/')({
  head: () => meta({ label: 'Editar usuário' }),
  component: RouteComponent,
});

type Schema = import('zod').input<typeof userSchemas.update>;

function RouteComponent() {
  const router = useRouter();
  const { id } = Route.useParams();
  const { data } = useUser({ id });
  const { mutate: save, error, isSuccess, isPending: isSubmitting } = useUpdateUser();

  const form = useForm<Schema>({
    resolver: zodResolver(userSchemas.update) as any,
    shouldUseNativeValidation: false,
    values: data as unknown as Schema,
  });

  const handleSubmit = (values: Schema) => save({ id, data: values } as any);

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error?.status === 422) {
      const { errors } = error.value as unknown as ValidationError<Schema>;

      if (errors) {
        for (const [key, message] of Object.entries(errors)) {
          form.setError(key as keyof Schema, { message: message as string });
        }
      }
    }
  }, [error, form]);

  return (
    <Content
      breadcrumbs={{
        icon: 'users',
        title: (
          <>
            Editar usuário{' '}
            <CopyText className="ml-2" value={id} label={`${id.slice(0, 8)}...`} />
          </>
        ),
        items: [
          { label: 'Listagem', route: '/users' },
          { label: 'Editar', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title="Dados do usuário"
        description="Nome e e-mail não podem ser alterados aqui."
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <FormInput
            name={'name' as any}
            control={form.control as any}
            label="Nome"
            disabled
          />
          <FormInput
            name={'email' as any}
            control={form.control as any}
            label="E-mail"
            disabled
          />
          <FormSelect
            name="role"
            control={form.control}
            label="Perfil"
            options={userRoleOptions}
          />
          <FormSwitch name="isActive" control={form.control} label="Ativo" />
        </FormContent>
        <FormFooter>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="success"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
